import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useConversationView } from 'src/modules/shared/communications/hooks/use-conversation-view';
import { communicationsSearchSchema } from 'src/modules/shared/schemas/communications-search.schema';
import { Route as CommunicationsIndexRoute } from 'src/routes/communications/index';
import { useNotificationsMarkRead } from './api/notifications';
import {
  getNotificationGroupKey,
  groupNotificationItems,
} from '../utils/notification-group.util';
import { shouldPresentMessageNotification } from '../utils/notification-alert-policy.util';
import type { NotificationAlertGroup } from '../types/notification-group.types';
import type { NotificationAlertInput } from '../types/notification-alert.types';
import { useNotificationSoundCooldown } from './use-notification-sound-cooldown';
import { useNotificationAlertDeduplication } from './use-notification-alert-deduplication';

/** Owns bounded transient message cards; neither inbox recovery nor chat read state emits alerts. */
export const useMessageNotificationAlerts = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { user } = useCurrentUser(isAuthenticated && !isLoading);
  const registry = useConversationView();
  const navigate = useNavigate();
  const markRead = useNotificationsMarkRead();
  const [alerts, setAlerts] = useState<NotificationAlertGroup[]>([]);
  const visibleSince = useRef(Date.now());
  const account =
    isAuthenticated && !isLoading && user
      ? `${user.tenantId}:${user.id}`
      : null;
  const playWithCooldown = useNotificationSoundCooldown(account);
  const { hasSeen, remember, broadcast } =
    useNotificationAlertDeduplication(account);

  useEffect(() => {
    setAlerts([]);
    visibleSince.current = Date.now();
  }, [account]);

  useEffect(() => {
    const handleActivity = () => {
      visibleSince.current = Date.now();
      if (document.visibilityState !== 'visible' || !document.hasFocus()) {
        setAlerts([]);
      }
    };
    document.addEventListener('visibilitychange', handleActivity);
    window.addEventListener('focus', handleActivity);
    window.addEventListener('blur', handleActivity);
    return () => {
      document.removeEventListener('visibilitychange', handleActivity);
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('blur', handleActivity);
    };
  }, []);

  useEffect(() => {
    const running = alerts.filter((entry) => !entry.paused);
    if (!running.length) return;
    const nextExpiry = Math.min(...running.map((entry) => entry.expiresAt));
    const timer = window.setTimeout(
      () =>
        setAlerts((current) =>
          current.filter(
            (entry) => entry.paused === true || entry.expiresAt > Date.now()
          )
        ),
      Math.max(0, nextExpiry - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [alerts]);

  const dismiss = useCallback(
    (key: string) =>
      setAlerts((current) => current.filter((entry) => entry.key !== key)),
    []
  );
  const pause = useCallback((key: string, paused: boolean) => {
    setAlerts((current) =>
      current.map((entry) =>
        entry.key === key
          ? { ...entry, paused, expiresAt: Date.now() + 8000 }
          : entry
      )
    );
  }, []);
  const present = useCallback(
    ({ item, preferences, playSound }: NotificationAlertInput): boolean => {
      if (
        item.type !== 'MESSAGE' ||
        !item.metadata?.message ||
        !item.metadata.funnelConversationId
      ) {
        return false;
      }
      if (!isAuthenticated || isLoading) return true;
      const receipt = {
        tenantId: item.tenantId,
        userId: item.userId,
        messageId: item.metadata.message.id,
      };
      if (hasSeen(receipt)) {
        setAlerts((current) =>
          current.map((entry) => {
            if (
              entry.key !== getNotificationGroupKey(item) ||
              entry.items.some((represented) => represented.id === item.id) ||
              !entry.items.some(
                (represented) =>
                  represented.metadata?.message?.id === receipt.messageId
              )
            ) {
              return entry;
            }
            return { ...entry, items: [...entry.items.slice(-99), item] };
          })
        );
        return true;
      }
      remember(receipt);
      const now = Date.now();
      if (
        !user ||
        !shouldPresentMessageNotification(item, {
          tenantId: user.tenantId,
          userId: String(user.id),
          now,
          visibleSince: visibleSince.current,
          focused:
            document.visibilityState === 'visible' && document.hasFocus(),
          conversation: registry?.getView() ?? null,
        })
      ) {
        return true;
      }
      if (preferences.showAlert || preferences.playSound) broadcast(receipt);
      const key = getNotificationGroupKey(item);
      if (preferences.showAlert) {
        setAlerts((current) => {
          const previous = current.find((entry) => entry.key === key);
          const group = groupNotificationItems([
            ...(previous?.items ?? []).slice(-99),
            item,
          ]).find((entry) => entry.key === key);
          if (!group) return current;
          return [
            ...current.filter((entry) => entry.key !== key),
            { ...group, expiresAt: now + 8000, paused: previous?.paused },
          ].slice(-3);
        });
      }
      if (preferences.playSound) playWithCooldown(key, now, playSound);
      return true;
    },
    [
      user,
      registry,
      isAuthenticated,
      isLoading,
      hasSeen,
      remember,
      broadcast,
      playWithCooldown,
    ]
  );

  const open = useCallback(
    (group: NotificationAlertGroup) => {
      if (
        !isAuthenticated ||
        isLoading ||
        !user ||
        group.latest.tenantId !== user.tenantId ||
        group.latest.userId !== String(user.id)
      ) {
        return;
      }
      markRead.mutate({
        inboxIds: group.items
          .filter((item) => !item.readAt)
          .map((item) => item.id),
      });
      void navigate({
        to: CommunicationsIndexRoute.to,
        search: communicationsSearchSchema.parse({
          conversationId: group.latest.metadata?.funnelConversationId,
        }),
      });
      dismiss(group.key);
    },
    [dismiss, markRead, navigate, user, isAuthenticated, isLoading]
  );
  return { alerts, present, dismiss, open, pause };
};
